import dayjs from "dayjs";
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
import { errorMessages } from "../constants/frontendErrorMessages";

const GHANA_PHONE_NUMBER_PATTERN = /^\+?[0-9\s\-().]{4,20}$/;
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

// Validate MongoDB ObjectId format
export const isValidObjectId = (id) => /^[a-fA-F0-9]{24}$/.test(id);

// Validate email
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return errorMessages.INVALID_EMAIL;
  return "";
};

// FUNCTION: Validate Phone Number
export function validatePhone(phoneNumber) {
  if (!phoneNumber) {
    return errorMessages.REQUIRED_FIELD;
  }

  // NEW: Check for invalid characters (letters or special characters not allowed in phone numbers)
  // First, check if there are any letters (a-z, A-Z)
  if (/[a-zA-Z]/.test(phoneNumber)) {
    return "Phone number cannot contain letters";
  }
  
  // Check for other invalid characters (allow only: digits, spaces, plus sign, dashes, parentheses, dots)
  const VALID_PHONE_CHARS = /^[\d\s\+\-\(\)\.]+$/;
  if (!VALID_PHONE_CHARS.test(phoneNumber)) {
    return "Phone number contains invalid characters. Only numbers, spaces, +, -, (, ), and . are allowed";
  }

  // Extract only digits from the phone number
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // Check if there are at least 4 digits
  if (digitsOnly.length < 4) {
    return errorMessages.PHONE_NUMBER_MIN_DIGITS;
  }

  // Check maximum length (15 digits)
  if (digitsOnly.length > 15) {
    return "Phone number must be 15 digits or less";
  }

  // Check minimum valid phone number length (7 digits is standard minimum)
  if (digitsOnly.length < 7) {
    return "Phone number must be at least 7 digits";
  }

  return "";
}

// Validate email or phone number
const validateEmailOrPhone = (input) => {
  if (!input) {
    return errorMessages.REQUIRED_FIELD;
  } else if (validateEmail(input) === "") {
    // valid email
    return "";
  } else if (validatePhone(input) === "") {
    // valid phone number
    return "";
  }

  return errorMessages.INVALID_EMAIL_OR_PHONE; // invalid email or phone number
};

// Validate password
const validatePassword = (password) => {
  if (!PASSWORD_PATTERN.test(password)) return errorMessages.INVALID_PASSWORD;
  return "";
};

// Validate name (first name, last name, full name, job title, org name) - only letters and spaces
const validateName = (name, fieldType) => {
  const namePattern = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\s]+$/;

  const trimmedName = name.trim();

  if (!namePattern.test(trimmedName)) {
    if (fieldType === COMMON_FIELD_TYPES.FIRST_NAME) {
      return errorMessages.INVALID_FIRST_NAME;
    } else if (fieldType === COMMON_FIELD_TYPES.LAST_NAME) {
      return errorMessages.INVALID_LAST_NAME;
    } else if (fieldType === COMMON_FIELD_TYPES.JOB_TITLE) {
      return errorMessages.INVALID_JOB_TITLE;
    } else if (fieldType === COMMON_FIELD_TYPES.ORG_NAME) {
      return errorMessages.INVALID_ORG_NAME;
    } else {
      return errorMessages.INVALID_FULL_NAME;
    }
  }

  return "";
};

// FUNCTION: Validate number
const isValidNumber = (input) => {
  // Check if the input is of type "number" or can be converted to a valid number
  if (typeof input === "number" && !isNaN(input)) {
    return "";
  }
  if (
    typeof input === "string" &&
    !isNaN(parseFloat(input)) &&
    isFinite(input)
  ) {
    return "";
  }
  return errorMessages.INVALID_NUMBER;
};

// FUNCTION: Validate date
const isValidDate = (dateString) => {
  // If no date provided
  if (!dateString) return false;
  dateString = dayjs(dateString);

  // If it's a dayjs object, format it first
  if (dayjs.isDayjs(dateString)) {
    dateString = dateString.format("YYYY-MM-DD");
  }

  // Check format (YYYY-MM-DD)
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) return false;

  // Parse the date parts
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a new Date object and verify the values
  const date = new Date(year, month - 1, day); // month is 0-based in JavaScript
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to start of the day

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1900 && // Reasonable minimum year
    year <= 2100 && // Reasonable maximum year
    date >= today // Ensure the date is not in the past
  );
};

// FUNCTION: Validate time
const isValidTime = (time) => {
  // Regex for 12-hour format (e.g., 01:30 AM, 12:45 PM)
  const twelveHourRegex = /^(0[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)$/i;

  // Regex for 24-hour format (e.g., 14:50, 00:15)
  const twentyFourHourRegex = /^([01]\d|2[0-3]):([0-5][0-9])$/;

  return twelveHourRegex.test(time) || twentyFourHourRegex.test(time);
};

// FUNCTION: Ghana Nic Number Validation - Format Should be like this GHA-XXXXXXXXX-X
function isValidGhanaIdNumber(pin) {
  // Check if the input is a string and has a minimum length of 1
  if (typeof pin !== "string" || pin.length < 1) {
    return false;
  }
}

// FUNCTION: Validate field
export const validateField = (name, type, value, formData, requiredFields) => {
  if (!type) return "";

  let error = "";
  const fieldType = typeof type === "object" ? type.type : type; // Extract actual type

  console.log("fieldType", fieldType);

  switch (fieldType) {
    case COMMON_FIELD_TYPES.REQUIRED_FIELD:
      if (
        (!value || value?.length < 1 || Object?.keys(value)?.length < 1) &&
        requiredFields[name]
      ) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && typeof type === "object" && type.maxLength) {
        // Check max length if specified
        if (value.length > type.maxLength) {
          error = type.maxLengthError || errorMessages.MAX_LENGTH_EXCEEDED;
        }
      }
      break;

    case COMMON_FIELD_TYPES.BOOLEAN_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (
        !(!value && value !== false) &&
        !(value === false || value === true)
      )
        error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.CHECKBOX:
      const normalized =
        value === true || value === "true" || value === 1 || value === "1"
          ? true
          : value === false || value === 0 || value === "false" || value === "0"
          ? false
          : null;

      if (requiredFields[name] && normalized === false) {
        error = errorMessages.PRIVACY_CONCENT_ERROR;
      } else if (normalized === null) {
        error = errorMessages.INVALID_OPTION;
      }
      break;

    case COMMON_FIELD_TYPES.NUMBER_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (value) error = isValidNumber(value);
      break;

    case COMMON_FIELD_TYPES.FULL_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateName(value, COMMON_FIELD_TYPES.FULL_NAME);
      break;

    case COMMON_FIELD_TYPES.FIRST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value)
        error = validateName(value, COMMON_FIELD_TYPES.FIRST_NAME);
      break;

    case COMMON_FIELD_TYPES.LAST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateName(value, COMMON_FIELD_TYPES.LAST_NAME);
      break;

    case COMMON_FIELD_TYPES.JOB_TITLE:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) {
        error = validateName(value, COMMON_FIELD_TYPES.JOB_TITLE);
        // Also check max length if specified in object format
        if (
          !error &&
          typeof type === "object" &&
          type.maxLength &&
          value.length > type.maxLength
        ) {
          error = type.maxLengthError || errorMessages.MAX_LENGTH_EXCEEDED;
        }
      }
      break;

    case COMMON_FIELD_TYPES.ORG_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateName(value, COMMON_FIELD_TYPES.ORG_NAME);
      break;

    case COMMON_FIELD_TYPES.TEXT_FIELD: //! handle optional text
    case COMMON_FIELD_TYPES.TEXT: //! handle optional text
      if (requiredFields && requiredFields[name]) {
        // added null check //!
        if (!value || value.length < 1) {
          error = errorMessages.REQUIRED_FIELD;
        }
      }
      // else optional, no error even if empty
      break;

    //! Added URL validation
    case COMMON_FIELD_TYPES.URL:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && value.trim()) {
        // Validate URL format
        const trimmedValue = value.trim();

        const urlPattern =
          /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

        if (!urlPattern.test(trimmedValue)) {
          error = errorMessages.INVALID_URL;
        } else {
          try {
            const urlToValidate =
              trimmedValue.startsWith("http://") ||
              trimmedValue.startsWith("https://")
                ? trimmedValue
                : `https://${trimmedValue}`;

            const urlObj = new URL(urlToValidate);

            if (!urlObj.hostname || urlObj.hostname.length < 3) {
              error = errorMessages.INVALID_URL;
            }
          } catch {
            error = errorMessages.INVALID_URL;
          }
        }
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateEmail(value);
      break;

    case COMMON_FIELD_TYPES.PHONE:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePhone(value);
      break;

    case COMMON_FIELD_TYPES.PASSWORD_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePassword(value);
      break;

    case COMMON_FIELD_TYPES.CONFIRM_PASSWORD:
      if (requiredFields[name]) {
        if (!value) {
          error = errorMessages.REQUIRED_FIELD;
        } else if (value !== formData.password) {
          error = errorMessages.PASSWORD_MISMATCH;
        }
      } else if (value) {
        if (value !== formData.password) {
          error = errorMessages.PASSWORD_MISMATCH;
        }
      }
      break;
    // case COMMON_FIELD_TYPES.DROPDOWN:
    //   if ((!value || Object.keys(value).length === 0) && requiredFields[name]) {
    //     error = errorMessages.REQUIRED_FIELD;
    //   }
    //   //   else if (typeof value !== "object" || !value.hasOwnProperty("id"))
    //   //     error = errorMessages.INVALID_OPTION;
    //   break;
    case COMMON_FIELD_TYPES.DROPDOWN:
      console.log("Dropdown validation for:", name);
      console.log("Value:", value);
      console.log("Object.keys length:", value ? Object.keys(value).length : "no value");
      console.log("Required:", requiredFields[name]);
      
      if ((!value || Object.keys(value).length === 0) && requiredFields[name]) {
        console.log("❌ Validation failed - returning required error");
        error = errorMessages.REQUIRED_FIELD;
      } else {
        console.log("✅ Validation passed");
      }
      break;

    case COMMON_FIELD_TYPES.DATE_PICKER:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && !isValidDate(value)) {
        error = errorMessages.INVALID_DATE;
      }
      break;

    case COMMON_FIELD_TYPES.TIME_PICKER:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (!isValidTime(value)) error = errorMessages.INVALID_TIME;
      break;

    case COMMON_FIELD_TYPES.ID_NUMBER:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (isValidGhanaIdNumber(value)) {
        error = errorMessages.INVALID_ID_NUMBER;
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_OR_PHONE:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        error = validateEmailOrPhone(value);
      }
      break;

    case COMMON_FIELD_TYPES.CURRENCY:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value !== null && value !== undefined && value !== "") {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
          error = errorMessages.INVALID_NUMBER;
        } else if (numericValue <= 0) {
          error = errorMessages.INVALID_CURRENCY;
        }
      }
      break;

    default:
      break;
  }

  return error;
};
